import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import SEOMeta from '@/components/feature/SEOMeta';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const canonical = `${origin}/category/${slug}`;

  if (!category) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">分类不存在</h1>
          <Link to="/" className="text-primary-600 hover:underline">返回首页</Link>
        </div>
      </div>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: origin,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category.name,
        item: canonical,
      },
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 黄瓜传媒`,
    description: category.description,
    url: canonical,
    isPartOf: {
      '@type': 'WebSite',
      name: '黄瓜传媒',
      url: origin,
    },
    about: {
      '@type': 'Thing',
      name: category.name,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEOMeta
        title={`${category.name} - 黄瓜传媒`}
        description={category.description}
        keywords={`${category.name},黄瓜传媒${category.name},品牌传播,数字营销`}
        canonical={canonical}
      />
      <SchemaOrg schema={breadcrumbSchema} />
      <SchemaOrg schema={webpageSchema} />

      {/* Category header */}
      <div className="bg-background-100 border-b border-background-200/70">
        <div className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: category.name }]} />
            <div className="mt-4 md:mt-6">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 mb-3">
                {category.name}
              </h1>
              <p className="text-foreground-600 max-w-xl leading-relaxed">
                {category.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Category tabs */}
      <div className="w-full px-4 md:px-6 lg:px-10 py-6 md:py-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70 hover:text-primary-600 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Articles grid */}
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <article
                  key={article.id}
                  className="group flex flex-col bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300/60 hover:shadow-lg transition-all duration-300"
                >
                  <Link to={`/article/${article.id}`} className="relative aspect-[16/10] overflow-hidden flex-shrink-0">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </Link>
                  <div className="flex flex-col flex-1 p-5 md:p-6">
                    <div className="flex items-center gap-2 mb-3 text-xs text-foreground-500">
                      <span>{article.date}</span>
                      <span>·</span>
                      <span>{article.readTime}</span>
                    </div>
                    <h2 className="text-base font-semibold font-heading text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      <Link to={`/article/${article.id}`}>{article.title}</Link>
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-2 mt-auto">
                      <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-xs text-primary-700 font-medium">
                        {article.author.charAt(0)}
                      </div>
                      <span className="text-xs text-foreground-500">{article.author}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}